import { ChangeEvent, FormEvent, MouseEvent, useEffect, useMemo, useState } from "react";
import { ArrowRight, Ban, CheckCircle2, DollarSign, Landmark, Plus, Search, Send, WalletCards, XCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { httpServices as services, loadAllPages } from "@/services";
import type { BuyingPrice, CommercialDelivery, CommercialTransaction, FarmerBalance, FarmerPayment, PaymentRequest } from "@/types/domain";
import { FarmerAdvancesTab } from "@/features/accounts/FarmerAdvancesTab";
import { PageHeader } from "@/components/PageHeader";
import { ErrorState, LoadingState } from "@/components/shared/StatePanels";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const STATUS_COLORS: Record<string, string> = {
  CALCULATED: "border-amber-500/20 bg-amber-500/10 text-amber-600",
  APPROVED: "border-indigo-500/20 bg-indigo-500/10 text-indigo-600",
  POSTED: "border-green-500/20 bg-green-500/10 text-green-600",
  PENDING: "border-amber-500/20 bg-amber-500/10 text-amber-600",
  PAID: "border-green-500/20 bg-green-500/10 text-green-600",
  DRAFT: "border-slate-500/20 bg-slate-500/10 text-slate-600",
  SUBMITTED: "border-amber-500/20 bg-amber-500/10 text-amber-600",
  REJECTED: "border-red-500/20 bg-red-500/10 text-red-600",
  CANCELLED: "border-slate-500/20 bg-slate-500/10 text-slate-600",
};

const PRODUCT_FORMS = [
  { value: "DRY_CHERRY", label: "Dry Cherry" },
  { value: "KIBOKO", label: "Kiboko" },
  { value: "GREEN_BEAN", label: "Green Bean" },
  { value: "OTHER", label: "Other" },
];

const PROGRAMME_EXPENSE_CATEGORIES = [
  { value: "FIELD_OPERATIONS", label: "Field operations" },
  { value: "INPUTS", label: "Inputs" },
  { value: "TRANSPORT", label: "Transport" },
  { value: "FUEL", label: "Fuel" },
  { value: "NURSERY", label: "Nursery" },
  { value: "STAFF", label: "Staff" },
  { value: "MEALS", label: "Meals" },
  { value: "EQUIPMENT_MAINTENANCE", label: "Equipment maintenance" },
  { value: "ADMIN", label: "Administration" },
  { value: "OTHER", label: "Other" },
];

const emptyProgrammeExpenseForm = () => ({
  request_reference: "",
  expense_category: "FIELD_OPERATIONS",
  expense_date: new Date().toISOString().slice(0, 10),
  beneficiary_type: "SUPPLIER",
  beneficiary_id: "",
  purpose: "",
  amount: "",
  currency: "UGX",
  source_module: "",
  support_document_reference: "",
  notes: "",
});

async function loadAllDeliveries(): Promise<CommercialDelivery[]> {
  return loadAllPages((options) => services.harvest.listPhysicalDeliveries(options));
}

export default function AccountsPage() {
  const [buyingPrices, setBuyingPrices] = useState<BuyingPrice[]>();
  const [deliveries, setDeliveries] = useState<CommercialDelivery[]>();
  const [settlements, setSettlements] = useState<CommercialTransaction[]>();
  const [payments, setPayments] = useState<FarmerPayment[]>();
  const [programmeExpenses, setProgrammeExpenses] = useState<PaymentRequest[]>();
  const [balances, setBalances] = useState<FarmerBalance[]>();
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [showCreatePrice, setShowCreatePrice] = useState(false);
  const [showCreateSettlement, setShowCreateSettlement] = useState(false);
  const [showCreatePayment, setShowCreatePayment] = useState(false);
  const [showCreateProgrammeExpense, setShowCreateProgrammeExpense] = useState(false);

  const [priceForm, setPriceForm] = useState({
    crop: "COFFEE", product_form: "DRY_CHERRY", quality_grade: "", unit_price: "",
    currency: "UGX", unit: "kg", effective_from: "", effective_until: "", notes: "",
  });
  const [settlementForm, setSettlementForm] = useState({
    farmer_id: "", delivery_id: "", buying_price_id: "", transaction_reference: "",
    transaction_date: new Date().toISOString().slice(0, 10),
    accepted_quantity: "", unit_price: "", currency: "UGX",
  });
  const [paymentForm, setPaymentForm] = useState({
    farmer_id: "", payment_reference: "", amount: "",
    currency: "UGX", payment_method: "MOBILE_MONEY", transaction_id: "",
  });
  const [programmeExpenseForm, setProgrammeExpenseForm] = useState(emptyProgrammeExpenseForm());

  const refreshFarmerBalances = async () => {
    try {
      const rows = await loadAllPages((options) => services.accounts.listFarmerBalances(options), {}, (row) => row.farmerId);
      setBalances(rows);
    } catch (reason: unknown) {
      const message = reason instanceof Error ? reason.message : "Farmer balances could not be refreshed.";
      toast.error(message);
    }
  };

  const loadAll = async () => {
    setLoadError("");
    try {
      const [prices, deliveryRows, settlementRows, paymentRows, programmeExpenseRows, balanceRows] = await Promise.all([
        services.accounts.listBuyingPrices(),
        loadAllDeliveries(),
        loadAllPages((options) => services.accounts.listSettlements(options)),
        loadAllPages((options) => services.accounts.listFarmerPayments(options)),
        loadAllPages((options) => services.accounts.listProgrammeExpenses(options)),
        loadAllPages((options) => services.accounts.listFarmerBalances(options), {}, (row) => row.farmerId),
      ]);
      setBuyingPrices(prices);
      setDeliveries(deliveryRows);
      setSettlements(settlementRows);
      setPayments(paymentRows);
      setProgrammeExpenses(programmeExpenseRows);
      setBalances(balanceRows);
    } catch (reason: unknown) {
      const message = reason instanceof Error ? reason.message : "Accounts data could not be loaded.";
      setLoadError(message);
      toast.error(message);
    }
  };

  useEffect(() => { void loadAll(); }, []);

  const verifiedDeliveries = useMemo(
    () => (deliveries || []).filter((delivery) => delivery.status === "VERIFIED"),
    [deliveries],
  );

  const selectedDelivery = deliveries?.find((delivery) => delivery.id === settlementForm.delivery_id);
  const effectiveBuyingPrices = (buyingPrices || []).filter((price) => {
    const day = selectedDelivery
      ? new Date(selectedDelivery.deliveredAt).getTime()
      : new Date(`${settlementForm.transaction_date}T12:00:00`).getTime();
    const start = new Date(price.effectiveFrom).getTime();
    const end = price.effectiveUntil ? new Date(price.effectiveUntil).getTime() : Number.POSITIVE_INFINITY;
    const formMatches = !selectedDelivery || price.productForm === selectedDelivery.productForm;
    const gradeMatches = !selectedDelivery?.qualityGrade || !price.qualityGrade || price.qualityGrade === selectedDelivery.qualityGrade;
    const unitMatches = !selectedDelivery || price.unit === selectedDelivery.unit;
    return price.status === "ACTIVE" && day >= start && day <= end && formMatches && gradeMatches && unitMatches;
  });

  const totalOutstanding = (balances || []).reduce((sum, row) => sum + row.totalOutstanding, 0);
  const totalDue = (balances || []).reduce((sum, row) => sum + row.totalDue, 0);
  const totalPaid = (balances || []).reduce((sum, row) => sum + row.totalPaid, 0);
  const pendingPayments = (payments || []).filter((payment) => payment.status === "PENDING" || payment.status === "APPROVED").length;
  // Canonical programme expense rows only. The endpoint already scopes to programme
  // expenses; accepting missing requestKind keeps legacy rows visible during migration.
  const canonicalProgrammeExpenses = (programmeExpenses || []).filter(
    (expense) => !expense.requestKind || expense.requestKind === "PROGRAMME_EXPENSE",
  );
  const programmePaid = canonicalProgrammeExpenses
    .filter((expense) => expense.status === "PAID")
    .reduce((sum, expense) => sum + expense.amount, 0);
  const programmePending = canonicalProgrammeExpenses.filter((expense) =>
    ["DRAFT", "SUBMITTED", "APPROVED"].includes(expense.status),
  ).length;

  const filteredProgrammeExpenses = canonicalProgrammeExpenses.filter((row) =>
    !search ||
    row.requestReference.toLowerCase().includes(search.toLowerCase()) ||
    row.beneficiaryType.toLowerCase().includes(search.toLowerCase()) ||
    row.purpose.toLowerCase().includes(search.toLowerCase()) ||
    (row.expenseCategory || "").toLowerCase().includes(search.toLowerCase()),
  );

  const filteredSettlements = (settlements || []).filter((row) =>
    !search ||
    row.transactionReference.toLowerCase().includes(search.toLowerCase()) ||
    (row.farmerName || "").toLowerCase().includes(search.toLowerCase()),
  );
  const filteredPayments = (payments || []).filter((row) =>
    !search ||
    row.paymentReference.toLowerCase().includes(search.toLowerCase()) ||
    (row.farmerName || "").toLowerCase().includes(search.toLowerCase()),
  );

  const handleCreatePrice = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await services.accounts.createBuyingPrice({
        crop: priceForm.crop.trim(),
        product_form: priceForm.product_form,
        quality_grade: priceForm.quality_grade.trim() || undefined,
        currency: priceForm.currency,
        unit: priceForm.unit,
        unit_price: Number(priceForm.unit_price),
        effective_from: new Date(priceForm.effective_from).toISOString(),
        effective_until: priceForm.effective_until ? new Date(priceForm.effective_until).toISOString() : undefined,
        notes: priceForm.notes.trim() || undefined,
      });
      toast.success("Farmer buying price saved");
      setShowCreatePrice(false);
      await loadAll();
    } catch (reason: unknown) {
      toast.error(reason instanceof Error ? reason.message : "Buying price could not be saved.");
    }
  };

  const handleCreateSettlement = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedDelivery || selectedDelivery.status !== "VERIFIED") {
      toast.error("Choose a verified delivery from Harvest.");
      return;
    }
    try {
      await services.accounts.createSettlement({
        farmer_id: selectedDelivery.farmerId,
        delivery_id: selectedDelivery.id,
        buying_price_id: settlementForm.buying_price_id || undefined,
        transaction_reference: settlementForm.transaction_reference,
        transaction_date: settlementForm.transaction_date,
        accepted_quantity: Number(settlementForm.accepted_quantity),
        unit_price: settlementForm.unit_price ? Number(settlementForm.unit_price) : undefined,
        currency: settlementForm.currency,
      });
      toast.success("Farmer settlement calculated");
      setShowCreateSettlement(false);
      setSettlementForm({
        farmer_id: "", delivery_id: "", buying_price_id: "", transaction_reference: "",
        transaction_date: new Date().toISOString().slice(0, 10),
        accepted_quantity: "", unit_price: "", currency: "UGX",
      });
      await loadAll();
    } catch (reason: unknown) {
      toast.error(reason instanceof Error ? reason.message : "Settlement could not be calculated.");
    }
  };

  const handleApproveSettlement = async (id: string) => {
    try {
      await services.accounts.approveSettlement(id);
      toast.success("Settlement approved");
      await loadAll();
    } catch (reason: unknown) {
      toast.error(reason instanceof Error ? reason.message : "Settlement could not be approved.");
    }
  };

  const handlePostSettlement = async (id: string) => {
    try {
      await services.accounts.postSettlement(id);
      toast.success("Settlement posted to farmer account");
      await loadAll();
    } catch (reason: unknown) {
      toast.error(reason instanceof Error ? reason.message : "Settlement could not be posted.");
    }
  };

  const handleCreatePayment = async (event: FormEvent) => {
    event.preventDefault();
    const settlement = settlements?.find((row) => row.id === paymentForm.transaction_id);
    if (!settlement || settlement.status !== "POSTED") {
      toast.error("Choose a posted farmer settlement.");
      return;
    }
    try {
      await services.accounts.createFarmerPayment({
        farmer_id: settlement.farmerId,
        payment_reference: paymentForm.payment_reference,
        amount: Number(paymentForm.amount),
        currency: paymentForm.currency,
        payment_method: paymentForm.payment_method,
        transaction_ids: [settlement.id],
      });
      toast.success("Farmer payment created");
      setShowCreatePayment(false);
      setPaymentForm({ farmer_id: "", payment_reference: "", amount: "", currency: "UGX", payment_method: "MOBILE_MONEY", transaction_id: "" });
      await loadAll();
    } catch (reason: unknown) {
      toast.error(reason instanceof Error ? reason.message : "Farmer payment could not be created.");
    }
  };

  const handleCreateProgrammeExpense = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await services.accounts.createProgrammeExpense({
        request_reference: programmeExpenseForm.request_reference.trim(),
        expense_category: programmeExpenseForm.expense_category,
        expense_date: programmeExpenseForm.expense_date,
        beneficiary_type: programmeExpenseForm.beneficiary_type,
        beneficiary_id: programmeExpenseForm.beneficiary_id.trim() || undefined,
        purpose: programmeExpenseForm.purpose.trim(),
        amount: Number(programmeExpenseForm.amount),
        currency: programmeExpenseForm.currency.trim() || "UGX",
        source_module: programmeExpenseForm.source_module.trim() || undefined,
        support_document_reference: programmeExpenseForm.support_document_reference.trim() || undefined,
        notes: programmeExpenseForm.notes.trim() || undefined,
      });
      toast.success("Programme expense created");
      setShowCreateProgrammeExpense(false);
      setProgrammeExpenseForm(emptyProgrammeExpenseForm());
      await loadAll();
    } catch (reason: unknown) {
      toast.error(reason instanceof Error ? reason.message : "Programme expense could not be created.");
    }
  };

  const handleSubmitProgrammeExpense = async (id: string) => {
    try {
      await services.accounts.submitProgrammeExpense(id);
      toast.success("Programme expense submitted for approval");
      await loadAll();
    } catch (reason: unknown) {
      toast.error(reason instanceof Error ? reason.message : "Programme expense could not be submitted.");
    }
  };

  const handleApproveProgrammeExpense = async (id: string) => {
    try {
      await services.accounts.approveProgrammeExpense(id);
      toast.success("Programme expense approved");
      await loadAll();
    } catch (reason: unknown) {
      toast.error(reason instanceof Error ? reason.message : "Programme expense could not be approved.");
    }
  };

  const handleRejectProgrammeExpense = async (id: string) => {
    const reason = window.prompt("Reason for rejecting this programme expense?")?.trim();
    if (!reason) return;
    try {
      await services.accounts.rejectProgrammeExpense(id, reason);
      toast.success("Programme expense rejected");
      await loadAll();
    } catch (reasonError: unknown) {
      toast.error(reasonError instanceof Error ? reasonError.message : "Programme expense could not be rejected.");
    }
  };

  const handleCancelProgrammeExpense = async (id: string) => {
    const reason = window.prompt("Reason for cancelling this programme expense?")?.trim();
    if (!reason) return;
    try {
      await services.accounts.cancelProgrammeExpense(id, reason);
      toast.success("Programme expense cancelled");
      await loadAll();
    } catch (reasonError: unknown) {
      toast.error(reasonError instanceof Error ? reasonError.message : "Programme expense could not be cancelled.");
    }
  };

  const handlePayProgrammeExpense = async (id: string) => {
    const method = window.prompt("Payment method (optional, e.g. BANK_TRANSFER, MOBILE_MONEY, CASH):", "");
    if (method === null) return;
    const reference = window.prompt("External payment reference (optional):", "");
    if (reference === null) return;
    try {
      await services.accounts.payProgrammeExpense(id, method.trim() || undefined, reference.trim() || undefined);
      toast.success("Programme expense marked paid");
      await loadAll();
    } catch (reason: unknown) {
      toast.error(reason instanceof Error ? reason.message : "Programme expense could not be marked paid.");
    }
  };

  const handleApprovePayment = async (id: string) => {
    try {
      await services.accounts.approveFarmerPayment(id);
      toast.success("Farmer payment approved");
      await loadAll();
    } catch (reason: unknown) {
      toast.error(reason instanceof Error ? reason.message : "Payment could not be approved.");
    }
  };

  const handleMarkPaid = async (id: string) => {
    try {
      await services.accounts.markFarmerPaymentPaid(id);
      toast.success("Farmer payment marked paid");
      await loadAll();
    } catch (reason: unknown) {
      toast.error(reason instanceof Error ? reason.message : "Payment could not be marked paid.");
    }
  };

  if (loadError && (!buyingPrices || !settlements || !payments || !programmeExpenses || !balances || !deliveries)) {
    return <ErrorState message={loadError} onRetry={loadAll} />;
  }
  if (!buyingPrices || !settlements || !payments || !programmeExpenses || !balances || !deliveries) {
    return <LoadingState label="Loading accounts" />;
  }

  return (
    <div className="space-y-6" data-testid="accounts-page">
      <PageHeader
        eyebrow="Programme finance"
        title="Accounts"
        description="Money owed, paid and spent by the ARISE programme. Harvest supplies verified delivery quantities; Accounts controls valuation, farmer payables, farmer payments and programme expenditure. Farmer harvest liabilities use Farmer Payments and are excluded from programme spend."
        action={
          <Button variant="outline" asChild>
            <Link to="/finance"><Landmark className="mr-2 size-4" />Open general ledger<ArrowRight className="ml-2 size-4" /></Link>
          </Button>
        }
      />

      <Card className="border-primary/15 bg-primary/[0.03]">
        <CardContent className="p-4 text-sm">
          <span className="font-semibold">Module boundary:</span>{" "}
          coffee is received and verified in <span className="font-semibold">Harvest</span>. Accounts cannot create or edit physical deliveries.
          It only consumes verified Harvest records to calculate farmer money and preserve the delivery → price → payable → payment audit trail.
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <Card><CardContent className="pt-6"><p className="text-sm text-muted-foreground">Farmer amount due</p><p className="mt-1 text-2xl font-bold">{totalDue.toLocaleString()} UGX</p></CardContent></Card>
        <Card><CardContent className="pt-6"><p className="text-sm text-muted-foreground">Paid to farmers</p><p className="mt-1 text-2xl font-bold">{totalPaid.toLocaleString()} UGX</p></CardContent></Card>
        <Card><CardContent className="pt-6"><p className="text-sm text-muted-foreground">Farmer outstanding</p><p className="mt-1 text-2xl font-bold">{totalOutstanding.toLocaleString()} UGX</p></CardContent></Card>
        <Card><CardContent className="pt-6"><p className="text-sm text-muted-foreground">Programme spend paid</p><p className="mt-1 text-2xl font-bold">{programmePaid.toLocaleString()} UGX</p></CardContent></Card>
        <Card><CardContent className="pt-6"><p className="text-sm text-muted-foreground">Open payment actions</p><p className="mt-1 text-2xl font-bold">{pendingPayments + programmePending}</p><p className="text-xs text-muted-foreground">{pendingPayments} farmer / {programmePending} programme</p></CardContent></Card>
      </div>

      <Tabs defaultValue="settlements">
        <TabsList className="flex h-auto flex-wrap">
          <TabsTrigger value="settlements">Farmer Settlements</TabsTrigger>
          <TabsTrigger value="payments">Farmer Payments</TabsTrigger>
          <TabsTrigger value="programme">Programme Expenses</TabsTrigger>
          <TabsTrigger value="prices">Farmer Buying Prices</TabsTrigger>
          <TabsTrigger value="balances">Farmer Balances</TabsTrigger>
          <TabsTrigger value="advances">Farmer Advances</TabsTrigger>
        </TabsList>

        <TabsContent value="settlements" className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold">Farmer settlement calculations</p>
              <p className="text-sm text-muted-foreground">Each calculation starts from a verified Harvest delivery and values the farmer's payable share.</p>
            </div>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 size-4 text-muted-foreground" />
                <Input className="w-64 pl-8" placeholder="Search settlements..." value={search} onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} />
              </div>
              <Button onClick={() => setShowCreateSettlement(true)}><Plus className="mr-1 size-4" />New settlement</Button>
            </div>
          </div>
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Reference</TableHead><TableHead>Farmer</TableHead><TableHead>Harvest delivery</TableHead><TableHead>Date</TableHead>
                <TableHead className="text-right">Applied price</TableHead><TableHead className="text-right">Gross value</TableHead>
                <TableHead className="text-right">Farmer payable</TableHead><TableHead className="text-right">ACZON share value</TableHead>
                <TableHead>Status</TableHead><TableHead>Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filteredSettlements.length === 0 ? (
                  <TableRow><TableCell colSpan={10} className="py-8 text-center text-muted-foreground">No farmer settlements yet.</TableCell></TableRow>
                ) : filteredSettlements.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono text-sm">{row.transactionReference}</TableCell>
                    <TableCell>{row.farmerName || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{row.deliveryReference || "—"}</TableCell>
                    <TableCell>{row.transactionDate}</TableCell>
                    <TableCell className="text-right font-mono">{row.appliedUnitPrice?.toLocaleString() || "—"} {row.currency}</TableCell>
                    <TableCell className="text-right font-mono">{row.grossAmount?.toLocaleString() || "—"}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{row.farmerPayableAmount?.toLocaleString() || "—"}</TableCell>
                    <TableCell className="text-right font-mono">{row.companyShareAmount?.toLocaleString() || "—"}</TableCell>
                    <TableCell><Badge variant="outline" className={STATUS_COLORS[row.status] || ""}>{row.status}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {row.status === "CALCULATED" && <Button size="sm" variant="ghost" onClick={() => void handleApproveSettlement(row.id)}><CheckCircle2 className="size-4" /></Button>}
                        {row.status === "APPROVED" && <Button size="sm" variant="ghost" onClick={() => void handlePostSettlement(row.id)}><WalletCards className="size-4" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="payments" className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div><p className="font-semibold">Farmer payments</p><p className="text-sm text-muted-foreground">Payments settle posted farmer liabilities and remain linked to the source settlement.</p></div>
            <Button onClick={() => setShowCreatePayment(true)}><Plus className="mr-1 size-4" />New farmer payment</Button>
          </div>
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Reference</TableHead><TableHead>Farmer</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Method</TableHead><TableHead>Ledger link</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {filteredPayments.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">No farmer payments yet.</TableCell></TableRow>
                ) : filteredPayments.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono text-sm">{row.paymentReference}</TableCell>
                    <TableCell>{row.farmerName || "—"}</TableCell>
                    <TableCell className="text-right font-mono">{row.amount.toLocaleString()} {row.currency}</TableCell>
                    <TableCell>{row.paymentMethod || "—"}</TableCell>
                    <TableCell>{row.financePaymentRequestId ? "Linked" : "Not linked"}</TableCell>
                    <TableCell><Badge variant="outline" className={STATUS_COLORS[row.status] || ""}>{row.status}</Badge></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {row.status === "PENDING" && <Button size="sm" variant="ghost" onClick={() => void handleApprovePayment(row.id)}><CheckCircle2 className="size-4" /></Button>}
                        {row.status === "APPROVED" && <Button size="sm" variant="ghost" onClick={() => void handleMarkPaid(row.id)}><DollarSign className="size-4" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="programme" className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold">Programme expenses</p>
              <p className="text-sm text-muted-foreground">Supplier, staff and other operational outflows incurred by the programme. Farmer harvest liabilities are settled through Farmer Payments and are excluded from this spend to avoid double counting.</p>
            </div>
            <Button onClick={() => setShowCreateProgrammeExpense(true)}><Plus className="mr-1 size-4" />New programme expense</Button>
          </div>
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Reference</TableHead><TableHead>Category</TableHead><TableHead>Expense date</TableHead>
                <TableHead>Beneficiary</TableHead><TableHead>Purpose</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filteredProgrammeExpenses.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="py-8 text-center text-muted-foreground">No programme expenses yet.</TableCell></TableRow>
                ) : filteredProgrammeExpenses.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono text-sm">{row.requestReference}</TableCell>
                    <TableCell>{PROGRAMME_EXPENSE_CATEGORIES.find((category) => category.value === row.expenseCategory)?.label || row.expenseCategory || "-"}</TableCell>
                    <TableCell>{row.expenseDate || "-"}</TableCell>
                    <TableCell><Badge variant="outline" title={row.beneficiaryId || undefined}>{row.beneficiaryType}</Badge></TableCell>
                    <TableCell className="max-w-md">{row.purpose}</TableCell>
                    <TableCell className="text-right font-mono">{row.amount.toLocaleString()} {row.currency}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_COLORS[row.status] || ""}>{row.status}</Badge>
                      {row.status === "REJECTED" && row.rejectionReason && (
                        <p className="mt-1 max-w-[16rem] text-xs text-muted-foreground">Rejected: {row.rejectionReason}</p>
                      )}
                      {row.status === "CANCELLED" && row.cancellationReason && (
                        <p className="mt-1 max-w-[16rem] text-xs text-muted-foreground">Cancelled: {row.cancellationReason}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {row.status === "DRAFT" && (
                          <>
                            <Button size="sm" variant="ghost" title="Submit" aria-label="Submit programme expense" onClick={() => void handleSubmitProgrammeExpense(row.id)}><Send className="size-4" /></Button>
                            <Button size="sm" variant="ghost" title="Cancel" aria-label="Cancel programme expense" onClick={() => void handleCancelProgrammeExpense(row.id)}><Ban className="size-4" /></Button>
                          </>
                        )}
                        {row.status === "SUBMITTED" && (
                          <>
                            <Button size="sm" variant="ghost" title="Approve" aria-label="Approve programme expense" onClick={() => void handleApproveProgrammeExpense(row.id)}><CheckCircle2 className="size-4" /></Button>
                            <Button size="sm" variant="ghost" title="Reject" aria-label="Reject programme expense" onClick={() => void handleRejectProgrammeExpense(row.id)}><XCircle className="size-4" /></Button>
                            <Button size="sm" variant="ghost" title="Cancel" aria-label="Cancel programme expense" onClick={() => void handleCancelProgrammeExpense(row.id)}><Ban className="size-4" /></Button>
                          </>
                        )}
                        {row.status === "APPROVED" && (
                          <>
                            <Button size="sm" variant="ghost" title="Mark paid" aria-label="Mark programme expense paid" onClick={() => void handlePayProgrammeExpense(row.id)}><DollarSign className="size-4" /></Button>
                            <Button size="sm" variant="ghost" title="Cancel" aria-label="Cancel programme expense" onClick={() => void handleCancelProgrammeExpense(row.id)}><Ban className="size-4" /></Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="prices" className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div><p className="font-semibold">Farmer buying-price schedule</p><p className="text-sm text-muted-foreground">This is the price ACZON uses to value farmer coffee. Buyer sales prices belong in Commercial, not here.</p></div>
            <Button onClick={() => setShowCreatePrice(true)}><Plus className="mr-1 size-4" />Add buying price</Button>
          </div>
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Crop</TableHead><TableHead>Form</TableHead><TableHead>Grade</TableHead><TableHead className="text-right">Price</TableHead><TableHead>From</TableHead><TableHead>Until</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {buyingPrices.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">No farmer buying prices configured.</TableCell></TableRow>
                ) : buyingPrices.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.crop}</TableCell><TableCell>{row.productForm}</TableCell><TableCell>{row.qualityGrade || "All grades"}</TableCell>
                    <TableCell className="text-right font-mono">{row.unitPrice.toLocaleString()} {row.currency}/{row.unit}</TableCell>
                    <TableCell>{new Date(row.effectiveFrom).toLocaleDateString()}</TableCell><TableCell>{row.effectiveUntil ? new Date(row.effectiveUntil).toLocaleDateString() : "Open-ended"}</TableCell>
                    <TableCell><Badge variant="outline">{row.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="balances">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Farmer</TableHead><TableHead className="text-right">Posted settlements</TableHead><TableHead className="text-right">Total due</TableHead><TableHead className="text-right">Paid</TableHead><TableHead className="text-right">Advance recovered</TableHead><TableHead className="text-right">Outstanding</TableHead></TableRow></TableHeader>
              <TableBody>
                {balances.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">No farmer balances yet.</TableCell></TableRow>
                ) : balances.map((row) => (
                  <TableRow key={row.farmerId}>
                    <TableCell>{row.farmerName || "Unknown farmer"}</TableCell>
                    <TableCell className="text-right">{row.postedTransactionCount}</TableCell>
                    <TableCell className="text-right font-mono">{row.totalDue.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-mono">{row.totalPaid.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-mono">{row.totalAdvanceRecovered.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{row.totalOutstanding.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="advances" className="space-y-4">
          <FarmerAdvancesTab onBalanceChange={refreshFarmerBalances} />
        </TabsContent>
      </Tabs>

      {showCreateProgrammeExpense && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowCreateProgrammeExpense(false)}>
          <Card className="w-full max-w-2xl" onClick={(e: MouseEvent) => e.stopPropagation()}><CardContent className="pt-6">
            <h2 className="mb-1 text-lg font-semibold">New programme expense</h2>
            <p className="mb-4 text-sm text-muted-foreground">Use this for programme purchases and operating costs such as inputs, transport, fuel, meals, equipment upkeep and administration. Farmer harvest liabilities are handled in Farmer Payments and must not be recorded here.</p>
            <form onSubmit={handleCreateProgrammeExpense} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Reference</Label><Input value={programmeExpenseForm.request_reference} onChange={(e: ChangeEvent<HTMLInputElement>) => setProgrammeExpenseForm({ ...programmeExpenseForm, request_reference: e.target.value })} placeholder="e.g. PE-2025-014" required /></div>
                <div><Label>Category</Label>
                  <Select value={programmeExpenseForm.expense_category} onValueChange={(value: string) => setProgrammeExpenseForm({ ...programmeExpenseForm, expense_category: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PROGRAMME_EXPENSE_CATEGORIES.map((category) => <SelectItem key={category.value} value={category.value}>{category.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Expense date</Label><Input type="date" value={programmeExpenseForm.expense_date} onChange={(e: ChangeEvent<HTMLInputElement>) => setProgrammeExpenseForm({ ...programmeExpenseForm, expense_date: e.target.value })} required /></div>
                <div><Label>Beneficiary type</Label>
                  <Select value={programmeExpenseForm.beneficiary_type} onValueChange={(value: string) => setProgrammeExpenseForm({ ...programmeExpenseForm, beneficiary_type: value, beneficiary_id: "" })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="SUPPLIER">Supplier</SelectItem><SelectItem value="EMPLOYEE">Employee / staff</SelectItem><SelectItem value="OTHER">Other</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Beneficiary ID (optional)</Label><Input value={programmeExpenseForm.beneficiary_id} onChange={(e: ChangeEvent<HTMLInputElement>) => setProgrammeExpenseForm({ ...programmeExpenseForm, beneficiary_id: e.target.value })} placeholder="Use when the beneficiary already exists in the system" /></div>
                <div><Label>Source module (optional)</Label><Input value={programmeExpenseForm.source_module} onChange={(e: ChangeEvent<HTMLInputElement>) => setProgrammeExpenseForm({ ...programmeExpenseForm, source_module: e.target.value })} placeholder="e.g. Inventory, Fleet, HR" /></div>
              </div>
              <div><Label>Purpose</Label><Input value={programmeExpenseForm.purpose} onChange={(e: ChangeEvent<HTMLInputElement>) => setProgrammeExpenseForm({ ...programmeExpenseForm, purpose: e.target.value })} placeholder="e.g. field team lunch, fertilizer supply, vehicle fuel, nursery materials" required /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Amount</Label><Input type="number" min="0.01" step="0.01" value={programmeExpenseForm.amount} onChange={(e: ChangeEvent<HTMLInputElement>) => setProgrammeExpenseForm({ ...programmeExpenseForm, amount: e.target.value })} required /></div>
                <div><Label>Currency</Label><Input value={programmeExpenseForm.currency} onChange={(e: ChangeEvent<HTMLInputElement>) => setProgrammeExpenseForm({ ...programmeExpenseForm, currency: e.target.value })} /></div>
                <div><Label>Supporting document</Label><Input value={programmeExpenseForm.support_document_reference} onChange={(e: ChangeEvent<HTMLInputElement>) => setProgrammeExpenseForm({ ...programmeExpenseForm, support_document_reference: e.target.value })} placeholder="Invoice / receipt ref" /></div>
              </div>
              <div><Label>Notes (optional)</Label><Input value={programmeExpenseForm.notes} onChange={(e: ChangeEvent<HTMLInputElement>) => setProgrammeExpenseForm({ ...programmeExpenseForm, notes: e.target.value })} /></div>
              <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setShowCreateProgrammeExpense(false)}>Cancel</Button><Button type="submit">Create expense</Button></div>
            </form>
          </CardContent></Card>
        </div>
      )}

      {showCreatePrice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowCreatePrice(false)}>
          <Card className="w-full max-w-xl" onClick={(e: MouseEvent) => e.stopPropagation()}><CardContent className="pt-6">
            <h2 className="mb-1 text-lg font-semibold">Add farmer buying price</h2>
            <p className="mb-4 text-sm text-muted-foreground">Used by Accounts to value verified Harvest deliveries. Commercial sales prices to external buyers will be managed separately.</p>
            <form onSubmit={handleCreatePrice} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Crop</Label><Input value={priceForm.crop} onChange={(e: ChangeEvent<HTMLInputElement>) => setPriceForm({ ...priceForm, crop: e.target.value })} required /></div>
                <div><Label>Product form</Label><Select value={priceForm.product_form} onValueChange={(value: string) => setPriceForm({ ...priceForm, product_form: value })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PRODUCT_FORMS.map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Grade</Label><Input value={priceForm.quality_grade} onChange={(e: ChangeEvent<HTMLInputElement>) => setPriceForm({ ...priceForm, quality_grade: e.target.value })} placeholder="Optional" /></div>
                <div><Label>Unit price</Label><Input type="number" min="0" step="0.01" value={priceForm.unit_price} onChange={(e: ChangeEvent<HTMLInputElement>) => setPriceForm({ ...priceForm, unit_price: e.target.value })} required /></div>
                <div><Label>Currency</Label><Input value={priceForm.currency} onChange={(e: ChangeEvent<HTMLInputElement>) => setPriceForm({ ...priceForm, currency: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Effective from</Label><Input type="datetime-local" value={priceForm.effective_from} onChange={(e: ChangeEvent<HTMLInputElement>) => setPriceForm({ ...priceForm, effective_from: e.target.value })} required /></div>
                <div><Label>Effective until</Label><Input type="datetime-local" value={priceForm.effective_until} onChange={(e: ChangeEvent<HTMLInputElement>) => setPriceForm({ ...priceForm, effective_until: e.target.value })} /></div>
              </div>
              <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setShowCreatePrice(false)}>Cancel</Button><Button type="submit">Save price</Button></div>
            </form>
          </CardContent></Card>
        </div>
      )}

      {showCreateSettlement && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowCreateSettlement(false)}>
          <Card className="w-full max-w-xl" onClick={(e: MouseEvent) => e.stopPropagation()}><CardContent className="pt-6">
            <h2 className="mb-1 text-lg font-semibold">Calculate farmer settlement</h2>
            <p className="mb-4 text-sm text-muted-foreground">The source delivery is read-only from Harvest. To change physical coffee quantities, return to Harvest.</p>
            <form onSubmit={handleCreateSettlement} className="space-y-3">
              <div><Label>Verified Harvest delivery</Label>
                <Select value={settlementForm.delivery_id} onValueChange={(value: string) => {
                  const delivery = deliveries.find((item) => item.id === value);
                  setSettlementForm({
                    ...settlementForm,
                    delivery_id: value,
                    farmer_id: delivery?.farmerId || "",
                    transaction_date: delivery?.deliveredAt.slice(0, 10) || settlementForm.transaction_date,
                    accepted_quantity: delivery ? String(delivery.acceptedQuantity ?? delivery.grossQuantity) : "",
                    buying_price_id: "",
                    unit_price: "",
                  });
                }}>
                  <SelectTrigger><SelectValue placeholder="Select verified delivery" /></SelectTrigger>
                  <SelectContent>{verifiedDeliveries.map((delivery) => <SelectItem key={delivery.id} value={delivery.id}>{delivery.deliveryReference} · {delivery.farmerName} · {delivery.acceptedQuantity ?? delivery.grossQuantity} {delivery.unit}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Settlement reference</Label><Input value={settlementForm.transaction_reference} onChange={(e: ChangeEvent<HTMLInputElement>) => setSettlementForm({ ...settlementForm, transaction_reference: e.target.value })} required /></div>
                <div><Label>Settlement date</Label><Input type="date" value={settlementForm.transaction_date} readOnly /></div>
              </div>
              <div><Label>Farmer buying price</Label>
                <Select value={settlementForm.buying_price_id} onValueChange={(value: string) => {
                  const price = buyingPrices.find((item) => item.id === value);
                  setSettlementForm({ ...settlementForm, buying_price_id: value, unit_price: "", currency: price?.currency || "UGX" });
                }}>
                  <SelectTrigger><SelectValue placeholder="Select effective price" /></SelectTrigger>
                  <SelectContent>{effectiveBuyingPrices.map((price) => <SelectItem key={price.id} value={price.id}>{price.productForm} · {price.qualityGrade || "All grades"} · {price.unitPrice.toLocaleString()} {price.currency}/{price.unit}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Verified accepted quantity</Label><Input value={settlementForm.accepted_quantity} readOnly /></div>
                <div><Label>Manual price if no schedule</Label><Input type="number" min="0" step="0.01" value={settlementForm.unit_price} onChange={(e: ChangeEvent<HTMLInputElement>) => setSettlementForm({ ...settlementForm, unit_price: e.target.value, buying_price_id: "" })} disabled={!!settlementForm.buying_price_id} /></div>
              </div>
              <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setShowCreateSettlement(false)}>Cancel</Button><Button type="submit" disabled={!settlementForm.delivery_id || (!settlementForm.buying_price_id && !settlementForm.unit_price)}>Calculate payable</Button></div>
            </form>
          </CardContent></Card>
        </div>
      )}

      {showCreatePayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowCreatePayment(false)}>
          <Card className="w-full max-w-lg" onClick={(e: MouseEvent) => e.stopPropagation()}><CardContent className="pt-6">
            <h2 className="mb-1 text-lg font-semibold">Create farmer payment</h2>
            <p className="mb-4 text-sm text-muted-foreground">A payment is created only against a posted farmer settlement.</p>
            <form onSubmit={handleCreatePayment} className="space-y-3">
              <div><Label>Posted settlement</Label>
                <Select value={paymentForm.transaction_id} onValueChange={(value: string) => {
                  const settlement = settlements.find((item) => item.id === value);
                  setPaymentForm({
                    ...paymentForm,
                    transaction_id: value,
                    farmer_id: settlement?.farmerId || "",
                    amount: settlement?.farmerPayableAmount != null ? String(settlement.farmerPayableAmount) : "",
                    currency: settlement?.currency || "UGX",
                  });
                }}>
                  <SelectTrigger><SelectValue placeholder="Select posted settlement" /></SelectTrigger>
                  <SelectContent>{settlements.filter((row) => row.status === "POSTED").map((row) => <SelectItem key={row.id} value={row.id}>{row.transactionReference} · {row.farmerName || "Farmer"} · {row.farmerPayableAmount?.toLocaleString() || "—"} {row.currency}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Payment reference</Label><Input value={paymentForm.payment_reference} onChange={(e: ChangeEvent<HTMLInputElement>) => setPaymentForm({ ...paymentForm, payment_reference: e.target.value })} required /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Amount</Label><Input type="number" step="0.01" value={paymentForm.amount} onChange={(e: ChangeEvent<HTMLInputElement>) => setPaymentForm({ ...paymentForm, amount: e.target.value })} required /></div>
                <div><Label>Currency</Label><Input value={paymentForm.currency} readOnly /></div>
                <div><Label>Method</Label><Input value={paymentForm.payment_method} onChange={(e: ChangeEvent<HTMLInputElement>) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })} /></div>
              </div>
              <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setShowCreatePayment(false)}>Cancel</Button><Button type="submit" disabled={!paymentForm.transaction_id}>Create payment</Button></div>
            </form>
          </CardContent></Card>
        </div>
      )}
    </div>
  );
}
